import { useGetClientRowQuery, useUpdateClientRowMutation } from '@/features/clients/clientsApi';
import { useGetDealRowQuery, useUpdateDealRowMutation } from '@/features/deals/dealsApi';
import { useUpdateRelationshipCompanyMutation } from '@/features/relationshipCompanies/relationshipCompaniesApi';
import {
	useGetFoldersAndFilesQuery,
	useLazyGetFoldersAndFilesQuery,
	useLazySearchFoldersAndFilesQuery,
} from '@/features/msGraph/msGraphApi';
import { getSharepointError } from '@/features/msGraph/helper';
import { setSharepointError } from '@/features/table/attachmentsTableSlice';
import { useGetBrokerGroupsQuery, useGetUserDetailsQuery } from '@/features/user/userApi';
import { folderStructure, tryDecodeFilePath } from '@/utils/constants';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const isItemNotFoundError = (query) =>
	query.isError &&
	query.error?.code?.toString()?.toLowerCase()?.includes('itemnotfound');

const getCorrectFolderOwner = ({ deal, client, relationshipCompany }) => {
	const dispatch = useDispatch();
	const sharepointErrors = useSelector((state) => state.attachmentsTable.sharepointErrors);

	const isRelationshipCompany = Boolean(relationshipCompany);
	// The documents tab wraps the client row with insight and broker. Other callers pass the row.
	const clientRecord = client?.clientRow ?? client;
	const dealOwnerDocId = deal?.ownerDocId;
	const relationshipCompanyOwnerDocId = relationshipCompany?.ownerDocId;
	const clientOwnerDocId = client?.ownerDocId ?? clientRecord?.ownerDocId;

	const [updateClientRow] = useUpdateClientRowMutation();
	const [updateDealRow] = useUpdateDealRowMutation();
	const [updateRelationshipCompany] = useUpdateRelationshipCompanyMutation();

	const [loadingDealDrives, setLoadingDealDrives] = useState(false);
	const [loadingRelationshipCompanyDrives, setLoadingRelationshipCompanyDrives] = useState(false);
	const [loadingClientDrives, setLoadingClientDrives] = useState(false);

	const loading =
    loadingDealDrives ||
    loadingRelationshipCompanyDrives ||
    loadingClientDrives;

	const dealOwnerQuery = useGetUserDetailsQuery(
		{ userId: dealOwnerDocId },
		{ skip: !dealOwnerDocId || isRelationshipCompany },
	);

	const relationshipCompanyOwnerQuery = useGetUserDetailsQuery(
		{ userId: relationshipCompanyOwnerDocId },
		{ skip: !relationshipCompanyOwnerDocId },
	);

	const clientOwnerQuery = useGetUserDetailsQuery(
		{ userId: clientOwnerDocId },
		{ skip: !clientOwnerDocId },
	);

	const brokerGroupIds = useMemo(() => {
		const dealBrokerGroupIds = dealOwnerQuery.data?.brokerGroupArray ?? [];
		const relationshipCompanyBrokerGroupIds =
			relationshipCompanyOwnerQuery.data?.brokerGroupArray ?? [];
		const clientBrokerGroupIds = clientOwnerQuery.data?.brokerGroupArray ?? [];
		return Array.from(
			new Set(
				dealBrokerGroupIds.concat(
					relationshipCompanyBrokerGroupIds,
					clientBrokerGroupIds,
				),
			),
		);
	}, [dealOwnerQuery.data, relationshipCompanyOwnerQuery.data, clientOwnerQuery.data]);

	const brokerGroupsQuery = useGetBrokerGroupsQuery(
		{ ids: brokerGroupIds },
		{
			skip: brokerGroupIds.length === 0,
		},
	);

	const clientFolderDriveIds = useMemo(() => {
		const driveIds = (brokerGroupsQuery.data ?? [])
			.concat([
				dealOwnerQuery.data,
				relationshipCompanyOwnerQuery.data,
				clientOwnerQuery.data,
			])
			.map((b) => b?.clientFolderDriveId)
			.filter(Boolean);
		return Array.from(new Set(driveIds));
	}, [
		brokerGroupsQuery.data,
		dealOwnerQuery.data,
		relationshipCompanyOwnerQuery.data,
		clientOwnerQuery.data,
	]);

	const { data: clientRow } = useGetClientRowQuery(
		clientRecord?.clientId ?? clientRecord?.id,
		{
			skip: !clientRecord?.clientId && !clientRecord?.id,
		},
	);

	const { data: dealRow } = useGetDealRowQuery(deal?.dealId, {
		skip: !deal?.dealId || isRelationshipCompany,
	});

	const dealRowId = dealRow?.driveId;
	const relationshipCompanyRowId = relationshipCompany?.driveId;
	const clientRowId = clientRow?.driveId;

	const [getFolders] = useLazyGetFoldersAndFilesQuery();

	const clientFolderDriveId = useMemo(
		() =>
			relationshipCompany?.clientFolderDriveId ??
			dealRow?.clientFolderDriveId ??
			clientRow?.clientFolderDriveId,
		[
			relationshipCompany?.clientFolderDriveId,
			dealRow?.clientFolderDriveId,
			clientRow?.clientFolderDriveId,
		],
	);

	const dealFolderQuery = useGetFoldersAndFilesQuery(
		{
			itemId: dealRow?.driveId,
			driveId: dealRow?.clientFolderDriveId,
		},
		{ skip: isRelationshipCompany || !dealRow?.driveId || !dealRow?.clientFolderDriveId },
	);

	const relationshipCompanyFolderQuery = useGetFoldersAndFilesQuery(
		{
			itemId: relationshipCompany?.driveId,
			driveId: relationshipCompany?.clientFolderDriveId,
		},
		{ skip: !relationshipCompany?.driveId || !relationshipCompany?.clientFolderDriveId },
	);

	const clientFolderQuery = useGetFoldersAndFilesQuery(
		{
			itemId: clientRow?.driveId,
			driveId: clientRow?.clientFolderDriveId,
		},
		{ skip: !clientRow?.driveId || !clientRow?.clientFolderDriveId },
	);

	const getValidId = async ({ dealRow, clientRow, relationshipCompany }) => {
		const setLoading = relationshipCompany
			? setLoadingRelationshipCompanyDrives
			: dealRow
				? setLoadingDealDrives
				: setLoadingClientDrives;
		const row = relationshipCompany ?? dealRow ?? clientRow;
		setLoading(true);
		let ids = [];
		for (const driveId of clientFolderDriveIds) {
			const newId = await validateFolderDriveId(driveId, row?.driveId);
			if (newId && !ids.includes(newId)) {
				ids.push(newId);
			}
		}
		if (
			(!row?.clientFolderDriveId || !ids.includes(row?.clientFolderDriveId)) &&
			ids.length > 0
		) {
			try {
				if (relationshipCompany) {
					await updateRelationshipCompany({
						relationshipCompanyId: relationshipCompany?.id,
						properties: { clientFolderDriveId: ids[0] },
					}).unwrap();
				} else if (dealRow) {
					await updateDealRow({
						dealId: dealRow?.dealId,
						docId: dealRow?.id,
						properties: { clientFolderDriveId: ids[0] },
					}).unwrap();
				} else if (clientRow) {
					await updateClientRow({
						clientId: clientRow?.clientId,
						docId: clientRow?.id,
						properties: { clientFolderDriveId: ids[0] },
					}).unwrap();
				}
			} catch {
				// The update failed, Loading is cleared below...
			}
			//If we didn't find the folder and there are drives against the object, report the error..
		} else if (ids.length === 0 && clientFolderDriveIds.length > 0) {
			const error = relationshipCompany
				? relationshipCompanyFolderQuery.error
				: dealRow
					? dealFolderQuery.error
					: clientFolderQuery.error;
			const sharepointError = getSharepointError(error);
			const objectId = relationshipCompany?.id ?? dealRow?.dealId ?? clientRow?.clientId;
			if (
				sharepointError &&
				objectId &&
				!sharepointErrors.some((item) => item.objectId === objectId)
			) {
				dispatch(setSharepointError({ ...sharepointError, objectId }));
			}
		}
		setLoading(false);
	};

	useEffect(() => {
		if (isItemNotFoundError(dealFolderQuery)) {
			getValidId({ dealRow });
		}
	}, [dealFolderQuery.isError, dealFolderQuery.error]);
	
	useEffect(() => {
		if (isItemNotFoundError(relationshipCompanyFolderQuery)) {
			getValidId({ relationshipCompany });
		}
	}, [relationshipCompanyFolderQuery.isError, relationshipCompanyFolderQuery.error]);
	
	useEffect(() => {
		if (isItemNotFoundError(clientFolderQuery)) {
			getValidId({ clientRow });
		}
	}, [clientFolderQuery.isError, clientFolderQuery.error]);

	const validateFolderDriveId = async (folderDriveId, itemId) => {
		if (!folderDriveId || !itemId) {
			return;
		}

		try {
			await getFolders({
				driveId: folderDriveId,
				itemId,
			}).unwrap();

			return folderDriveId;
		} catch (error) {
			if (error?.code?.toString()?.toLowerCase() === 'itemnotfound') {
				return;
			}

			console.error(`Error validating folder drive ID ${folderDriveId}:`, error);
		}
	};

	const row = relationshipCompany ?? dealRow ?? clientRow;
	const notesDriveId = row?.notesDriveId;

	const [searchFoldersAndFiles] = useLazySearchFoldersAndFilesQuery();

	const resolveNotesDriveId = useCallback(async () => {
		if (!row) return null;

		let resolvedNotesDriveId = row.notesDriveId;
		const resolvedDriveId = relationshipCompany
			? relationshipCompanyRowId
			: dealRow
				? dealRowId
				: clientRowId;

		if (!resolvedDriveId || !clientFolderDriveId) return null;

		if (!resolvedNotesDriveId) {
			const path =
				!relationshipCompany && !dealRow ? folderStructure.clientNotes : folderStructure.notes;

			const { folders = [] } = await searchFoldersAndFiles({
				itemId: resolvedDriveId,
				driveId: clientFolderDriveId,
				searchText: decodeURI(path.normalize()),
				exact: true,
			}).unwrap();

			const notesFolder = folders.find(
				(folder) => folder?.folder && tryDecodeFilePath(folder?.name, path),
			);

			resolvedNotesDriveId = notesFolder?.id || null;
		}

		if (resolvedNotesDriveId) {
			if (relationshipCompany) {
				await updateRelationshipCompany({
					relationshipCompanyId: relationshipCompany?.id,
					properties: { notesDriveId: resolvedNotesDriveId },
				}).unwrap();
			} else if (dealRow) {
				await updateDealRow({
					dealId: dealRow?.dealId,
					docId: dealRow?.id,
					properties: { notesDriveId: resolvedNotesDriveId },
				}).unwrap();
			} else if (clientRow) {
				await updateClientRow({
					clientId: clientRow?.clientId,
					docId: clientRow?.id,
					properties: { notesDriveId: resolvedNotesDriveId },
				}).unwrap();
			}
		}

		return resolvedNotesDriveId;
	}, [
		row,
		relationshipCompany,
		dealRow,
		clientRow,
		clientFolderDriveId,
		relationshipCompanyRowId,
		dealRowId,
		clientRowId,
		searchFoldersAndFiles,
	]);

	useEffect(() => {
		if (!notesDriveId && (relationshipCompany || dealRow || clientRow)) {
			resolveNotesDriveId();
		}
	}, [notesDriveId, relationshipCompany, dealRow, clientRow, resolveNotesDriveId]);

	return {
		correctOwner: clientOwnerDocId ?? relationshipCompanyOwnerDocId ?? dealOwnerDocId,
		loading,
		clientFolderDriveId,
	};
};

export default getCorrectFolderOwner;
